import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { t } from '../core/i18n.js';
import { showFormError, clearFormError, fieldFromApiError } from '../core/formError.js';

// Registration intents offered on the register form. They are not platform
// roles — the server keeps every signup INDIVIDUAL; the intent only opts
// landlords in server-side and steers the post-register landing.
const INTENTS = ['landlord', 'tenant', 'agency'];

export class LoginView extends Component {
  constructor() {
    super();
    // `error`/`busy` are intentionally NOT in state — handled via DOM so the
    // form re-renders only when the user toggles login<->register mode and
    // never on submit failure (keeps email/password values intact).
    this.state = { mode: 'login', intent: 'landlord' };
  }

  _intentPickerHtml() {
    return `
      <div class="field">
        <label>${esc(t('login.intent_label'))}</label>
        <div class="path-cards" data-intent-picker>
          ${INTENTS.map((key) => `
            <button type="button" class="path-card ${this.state.intent === key ? 'path-card-selected' : ''}"
                    data-intent="${esc(key)}" aria-pressed="${this.state.intent === key ? 'true' : 'false'}">
              <strong>${esc(t('login.intent_' + key))}</strong>
              <span class="muted">${esc(t('login.intent_' + key + '_hint'))}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  template() {
    const isLogin = this.state.mode === 'login';
    const submitLabel = isLogin ? t('login.sign_in') : t('login.create_account');
    const switchLabel = isLogin ? t('login.need_account') : t('login.have_account');
    return `
      <section class="stack">
        <div class="card stack" data-role="login-card">
          <h2>${esc(submitLabel)}</h2>
          <div data-form-error></div>
          <form class="stack" data-form>
            ${!isLogin ? `
              <div class="field">
                <label for="full_name">${esc(t('login.full_name'))}</label>
                <input class="input" type="text" id="full_name" name="full_name" autocomplete="name" required />
              </div>
              ${this._intentPickerHtml()}
            ` : ''}
            <div class="field">
              <label for="email">${esc(t('login.email'))}</label>
              <input class="input" type="email" id="email" name="email" autocomplete="email" required />
            </div>
            <div class="field">
              <label for="password">${esc(t('login.password'))}</label>
              <input class="input" type="password" id="password" name="password"
                autocomplete="${isLogin ? 'current-password' : 'new-password'}" required />
            </div>
            <button type="submit" class="btn btn-primary btn-block" data-submit>
              <span data-submit-label>${esc(submitLabel)}</span>
            </button>
          </form>
          <div class="row" style="justify-content:center">
            <button type="button" class="btn btn-ghost" data-action="toggle-mode">
              ${esc(switchLabel)}
            </button>
          </div>
        </div>
      </section>
    `;
  }

  mount() {
    this.el.querySelector('[data-form]').addEventListener('submit', (e) => this._submit(e));
    this.el.querySelector('[data-action="toggle-mode"]').addEventListener('click', () => {
      this.setState({ mode: this.state.mode === 'login' ? 'register' : 'login' });
    });
    // Intent is switched DOM-only (no setState) so a pick never re-renders
    // the form and wipes already-typed name/email/password values.
    this.el.querySelectorAll('[data-intent]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.state.intent = btn.getAttribute('data-intent');
        this.el.querySelectorAll('[data-intent]').forEach((b) => {
          const selected = b === btn;
          b.classList.toggle('path-card-selected', selected);
          b.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      });
    });
    const emailInput = this.el.querySelector('#email');
    if (emailInput && typeof emailInput.focus === 'function') emailInput.focus();
  }

  _setBusy(busy) {
    const btn = this.el.querySelector('[data-submit]');
    const label = this.el.querySelector('[data-submit-label]');
    if (btn) btn.disabled = busy;
    if (label) label.textContent = busy
      ? t('common.loading')
      : (this.state.mode === 'login' ? t('login.sign_in') : t('login.create_account'));
  }

  async _submit(e) {
    e.preventDefault();
    const card = this.el.querySelector('[data-role="login-card"]');
    const btn = this.el.querySelector('[data-submit]');
    if (btn && btn.disabled) return;
    clearFormError(card);
    this._setBusy(true);
    const form = new FormData(e.target);
    const email = String(form.get('email') || '').trim();
    const password = String(form.get('password') || '');
    const intent = this.state.intent;
    try {
      let session;
      let destination = null;
      if (this.state.mode === 'login') {
        session = await api.post('/auth/login', { email, password });
      } else {
        session = await api.post('/auth/register', {
          email,
          password,
          full_name: String(form.get('full_name') || '').trim(),
          // Every new account is INDIVIDUAL on the platform; whether
          // they act as landlord or tenant emerges from the properties
          // / tenancies they hold. (Agency staff are linked separately.)
          roles: ['INDIVIDUAL'],
          intent,
        });
        destination = auth.landingPathForIntent(intent);
      }
      auth.set(session);
      window.location.hash = '#' + (destination || auth.defaultPath());
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('common.network_error');
      const known = this.state.mode === 'login'
        ? ['email', 'password']
        : ['email', 'password', 'full_name'];
      const field = err instanceof ApiError ? fieldFromApiError(err, known) : null;
      // For login, an "Invalid credentials" 401 has no field — point at password.
      const fallback = (this.state.mode === 'login' && err instanceof ApiError && err.status === 401)
        ? 'password' : null;
      showFormError(card, message, { field: field || fallback });
      this._setBusy(false);
    }
  }
}
